import { axiosLib } from '@/lib/axios';
import { showToast } from '@/services/toast-service';
import { getColocApiCallProperties } from '@/lib/coloc-api';
import type { DiscountFormData } from '../../types/discount';

export const useUpdateDiscount = (contractId: number, refreshDiscounts: () => Promise<void>) => {
  const updateDiscount = async (discountId: number, data: DiscountFormData) => {
    try {
      const formData = new FormData();
      formData.append('discount[amount]', data.amount.toString());
      formData.append('discount[measurement]', data.unit === '€' ? 'absolute' : 'percent');
      formData.append('discount[period]', data.period);
      formData.append('discount[discount_category]', data.discount_category.toString());
      formData.append('discount[note]', data.note || '');

      if (data.period === 'recurring') {
        if (data.startDate) {
          const startDate =
            data.startDate instanceof Date
              ? data.startDate.toISOString().substring(0, 10)
              : data.startDate;
          formData.append('discount[start_date]', startDate);
        }
        if (data.endDate) {
          const endDate =
            data.endDate instanceof Date
              ? data.endDate.toISOString().substring(0, 10)
              : data.endDate;
          formData.append('discount[end_date]', endDate);
        }
      }

      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach((file: File) => {
          formData.append('discount[attachments][]', file);
        });
      }

      const response = await axiosLib.patch(
        `/admin/contracts/${contractId}/discounts/${discountId}`,
        formData,
        {
          params: {
            discount_category: data.discount_category.toString(),
          },
          ...getColocApiCallProperties(),
          headers: {
            ...getColocApiCallProperties().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data && response.data.success) {
        await refreshDiscounts();
        showToast.success({ text: response.data.success_message || 'Discount updated!' });
      } else {
        showToast.error({ text: response.data.error_message || 'Failed to update discount.' });
      }
    } catch (error: any) {
      console.error('Error updating discount:', error.response?.data || error.message);
      showToast.error({
        text: error.response?.data?.error_message || 'Failed to update discount.',
      });
    }
  };

  return { updateDiscount };
};
